import axios, { AxiosError, AxiosInstance } from 'axios';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
  UpdateUserRequest,
  AvailabilityTemplate,
  AvailabilityOverride,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CreateOverrideRequest,
  TimeSlot,
  TimeSlotStats,
  ClaimSlotRequest,
  UpdateSlotStatusRequest,
  AvailabilityWindow,
  ApiError,
  Message,
  SendMessageRequest,
  Notification,
} from '@/types';

// API base URL - update this to point to your Worker API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

// Token storage key
const TOKEN_KEY = 'kaiwatch_token';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Get runas email — check URL first, persist in sessionStorage across navigation
const RUNAS_KEY = 'kaiwatch_runas';

function getRunAsEmail(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('runas');
  if (fromUrl) {
    sessionStorage.setItem(RUNAS_KEY, fromUrl);
    return fromUrl;
  }
  return sessionStorage.getItem(RUNAS_KEY);
}

// Initialize on load — capture runas from URL immediately
getRunAsEmail();

// Request interceptor to add JWT token and runas header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const runAs = getRunAsEmail();
    if (runAs) {
      config.headers['X-RunAs'] = runAs;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const apiError: ApiError = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      code: error.response?.data?.code,
      status: error.response?.status,
    };

    // Handle 401 Unauthorized - clear token and redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      // Don't redirect here - let the auth hook handle it
    }

    return Promise.reject(apiError);
  }
);

// Token management
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', data);
    setToken(response.data.token);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    setToken(response.data.token);
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearToken();
    }
  },

  getMe: async (): Promise<User> => {
    const response = await api.get<{ user: User }>('/auth/me');
    return response.data.user;
  },

  getAvailableOptions: async (): Promise<{
    colors: { available: string[]; all: string[] };
    emojis: { available: string[]; all: string[] };
  }> => {
    const response = await api.get('/auth/available-options');
    return response.data;
  },
};

// Users API
export const usersApi = {
  list: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  get: async (id: string): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  update: async (id: string, data: UpdateUserRequest): Promise<User> => {
    const response = await api.patch<User>(`/users/${id}`, data);
    return response.data;
  },

  uploadProfilePicture: async (id: string, file: File): Promise<User> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<User>(`/users/${id}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Availability API
export const availabilityApi = {
  // Templates
  getTemplates: async (userId?: string): Promise<AvailabilityTemplate[]> => {
    const params = userId ? { user_id: userId } : {};
    const response = await api.get<AvailabilityTemplate[]>('/availability/templates', { params });
    return response.data;
  },

  createTemplate: async (data: CreateTemplateRequest): Promise<AvailabilityTemplate> => {
    const response = await api.post<AvailabilityTemplate>('/availability/templates', data);
    return response.data;
  },

  updateTemplate: async (id: string, data: UpdateTemplateRequest): Promise<AvailabilityTemplate> => {
    const response = await api.patch<AvailabilityTemplate>(`/availability/templates/${id}`, data);
    return response.data;
  },

  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/availability/templates/${id}`);
  },

  // Overrides
  getOverrides: async (userId?: string, startDate?: string, endDate?: string): Promise<AvailabilityOverride[]> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get<AvailabilityOverride[]>('/availability/overrides', { params });
    return response.data;
  },

  createOverride: async (data: CreateOverrideRequest): Promise<AvailabilityOverride> => {
    const response = await api.post<AvailabilityOverride>('/availability/overrides', data);
    return response.data;
  },

  deleteOverride: async (id: string): Promise<void> => {
    await api.delete(`/availability/overrides/${id}`);
  },

  // Get computed availability windows for a specific date
  getWindowsForDate: async (userId: string, date: string): Promise<AvailabilityWindow[]> => {
    const response = await api.get<AvailabilityWindow[]>(`/availability/windows`, {
      params: { user_id: userId, date },
    });
    return response.data;
  },
};

// Time Slots API
export const timeSlotsApi = {
  list: async (startDate?: string, endDate?: string, status?: string): Promise<TimeSlot[]> => {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (status) params.status = status;
    const response = await api.get<TimeSlot[]>('/time-slots', { params });
    return response.data;
  },

  claim: async (data: ClaimSlotRequest): Promise<TimeSlot> => {
    const response = await api.post<TimeSlot>(`/time-slots/${data.time_slot_id}/claim`);
    return response.data;
  },

  updateStatus: async (id: string, data: UpdateSlotStatusRequest): Promise<TimeSlot> => {
    const response = await api.patch<TimeSlot>(`/time-slots/${id}/status`, data);
    return response.data;
  },

  getStats: async (startDate?: string, endDate?: string): Promise<TimeSlotStats> => {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get<TimeSlotStats>('/time-slots/stats', { params });
    return response.data;
  },
};

// Messages API
export const messagesApi = {
  list: async (limit?: number, before?: string): Promise<Message[]> => {
    const params: Record<string, string | number> = {};
    if (limit) params.limit = limit;
    if (before) params.before = before;
    const response = await api.get<{ messages: Message[] }>('/messages', { params });
    return response.data.messages;
  },

  send: async (data: SendMessageRequest): Promise<Message> => {
    const response = await api.post<{ message: Message }>('/messages', data);
    return response.data.message;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/messages/${id}`);
  },
};

// Notifications API
export const notificationsApi = {
  list: async (unreadOnly?: boolean, limit?: number): Promise<Notification[]> => {
    const params: Record<string, string | number> = {};
    if (unreadOnly) params.unread_only = 'true';
    if (limit) params.limit = limit;
    const response = await api.get<{ notifications: Notification[] }>('/notifications', { params });
    return response.data.notifications;
  },

  unreadCount: async (): Promise<number> => {
    const response = await api.get<{ count: number }>('/notifications/unread-count');
    return response.data.count;
  },

  markRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await api.patch('/notifications/read-all');
  },
};

// Push API
export const pushApi = {
  getVapidPublicKey: async (): Promise<string> => {
    const response = await api.get<{ publicKey: string }>('/push/vapid-public-key');
    return response.data.publicKey;
  },

  subscribe: async (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<void> => {
    await api.post('/push/subscribe', subscription);
  },

  unsubscribe: async (endpoint: string): Promise<void> => {
    await api.delete('/push/unsubscribe', { data: { endpoint } });
  },
};

// Family Payments API
export interface FamilyPaymentData {
  user_id: string;
  hourly_rate: number;
  unpaid: {
    slots: any[];
    total_hours: number;
    total_amount: number;
  };
  paid: {
    slots: any[];
    total_hours: number;
    total_amount: number;
  };
}

export const familyPaymentsApi = {
  getPayments: async (userId: string): Promise<FamilyPaymentData> => {
    const response = await api.get<FamilyPaymentData>(`/timeslots/family-payments/${userId}`);
    return response.data;
  },

  markPaid: async (slotIds: string[]): Promise<void> => {
    await api.patch('/timeslots/mark-paid', { slot_ids: slotIds });
  },
};

export default api;
